import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const TARGET_TZ = "Europe/London";
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const INVALID_STRINGS = new Set(["null", "undefined", "NaN", "invalid"]);

const isDateOnly = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const isTimeOnly = (value) =>
  typeof value === "string" &&
  /^\d{1,2}:\d{2}(:\d{2})?$/.test(value);

const isISODateTime = (value) =>
  typeof value === "string" && value.includes("T");

const isInvalidString = (value) => {
  if (typeof value !== "string") return false;
  return INVALID_STRINGS.has(value.trim().toLowerCase());
};

export const formatTimeToAmPm = (timeString) => {
  if (!timeString || isInvalidString(timeString)) return "--";
  try {
    const parsed = dayjs.tz(String(timeString).trim(), "HH:mm:ss", TARGET_TZ);
    return parsed.isValid() ? parsed.format("h:mm a") : "--";
  } catch {
    return "--";
  }
};

export const formatTime = (time) => {
  if (!time || isInvalidString(time)) return "--";

  try {
    if (isTimeOnly(time)) {
      const normalized = time.length === 5 ? `${time}:00` : time;
      const parsed = dayjs.tz(normalized, "HH:mm:ss", TARGET_TZ);
      return parsed.isValid() ? parsed.format("h:mm a") : "--";
    }

    if (isISODateTime(time)) {
      const parsed = dayjs(time).tz(TARGET_TZ);
      return parsed.isValid() ? parsed.format("h:mm a") : "--";
    }

    const parsed = dayjs(time);
    return parsed.isValid() ? parsed.tz(TARGET_TZ).format("h:mm a") : "--";
  } catch {
    return "--";
  }
};

export const formatTimeRangeToAmPm = (startTime, endTime) => {
  if (!startTime || !endTime) return "--";
  const start = formatTime(startTime);
  const end = formatTime(endTime);
  return `${start} - ${end}`;
};

export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString || isInvalidString(dateTimeString)) return "--";
  try {
    const dateObj = dayjs(dateTimeString).tz(TARGET_TZ);
    if (!dateObj.isValid()) return "--";
    return `${dateObj.format("DD-MM-YYYY")}, ${dateObj.format("h:mm a")}`;
  } catch {
    return "--";
  }
};

export const formatDateField = (dateString) => {
  if (!dateString || isInvalidString(dateString)) return "—";

  if (isDateOnly(dateString)) {
    const [year, month, day] = dateString.split("-");
    const monthIdx = parseInt(month, 10) - 1;
    if (monthIdx < 0 || monthIdx > 11) return "Invalid Date";
    return `${day} ${MONTHS[monthIdx]} ${year}`;
  }

  try {
    const date = dayjs(dateString).tz(TARGET_TZ);
    if (!date.isValid()) return "Invalid Date";
    return date.format("DD MMM YYYY");
  } catch {
    return "Invalid Date";
  }
};

export const formatDateTimeRange = (startDateTime, endDateTime) => {
  if (!startDateTime || !endDateTime) return "";
  try {
    const start = dayjs(startDateTime).tz(TARGET_TZ);
    const end = dayjs(endDateTime).tz(TARGET_TZ);
    if (!start.isValid() || !end.isValid()) return "";
    return `${start.format("DD-MM-YYYY")} , ${start.format("h:mm a")} - ${end.format("h:mm a")}`;
  } catch {
    return "";
  }
};

export const formatTimeRange = (startTimeStr, endTimeStr) => {
  if (!startTimeStr || !endTimeStr) return "";
  return `${formatTime(startTimeStr)} - ${formatTime(endTimeStr)}`;
};

export const getDurationFromDateTime = (startDateTime, endDateTime) => {
  if (!startDateTime || !endDateTime) return "";

  try {
    const start = dayjs(startDateTime);
    const end = dayjs(endDateTime);

    if (!start.isValid() || !end.isValid()) return "";

    const durationMinutes = end.diff(start, "minute");
    if (durationMinutes <= 0) return "0m";

    const days = Math.floor(durationMinutes / (24 * 60));
    const hours = Math.floor((durationMinutes % (24 * 60)) / 60);
    const minutes = durationMinutes % 60;

    let durationText = "";
    if (days > 0) durationText += `${days}d `;
    if (hours > 0) durationText += `${hours}h `;
    if (minutes > 0) durationText += `${minutes}m`;

    return durationText.trim();
  } catch {
    return "";
  }
};

export const getDurationFromTime = (startTime, endTime) => {
  if (!startTime || !endTime || isInvalidString(startTime) || isInvalidString(endTime)) {
    return "";
  }

  try {
    const today = dayjs().tz(TARGET_TZ).format("YYYY-MM-DD");
    const normalizedStart = startTime.length === 5 ? `${startTime}:00` : startTime;
    const normalizedEnd = endTime.length === 5 ? `${endTime}:00` : endTime;

    let start = dayjs.tz(
      `${today} ${normalizedStart}`,
      "YYYY-MM-DD HH:mm:ss",
      TARGET_TZ
    );
    let end = dayjs.tz(
      `${today} ${normalizedEnd}`,
      "YYYY-MM-DD HH:mm:ss",
      TARGET_TZ
    );

    if (!start.isValid() || !end.isValid()) return "";

    if (end.isBefore(start)) {
      end = end.add(1, "day");
    }

    const durationMinutes = end.diff(start, "minute");
    if (durationMinutes <= 0) return "0m";

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    let durationText = "";
    if (hours > 0) durationText += `${hours}h `;
    if (minutes > 0) durationText += `${minutes}m`;

    return durationText.trim();
  } catch {
    return "";
  }
};

export const formatDateAndTime = (dateString, timeString) => {
  if (!dateString || !timeString) return "";

  try {
    const normalizedTime = timeString.length === 5 ? `${timeString}:00` : timeString;

    const combinedDateTime = dayjs.tz(
      `${dateString} ${normalizedTime}`,
      "YYYY-MM-DD HH:mm:ss",
      TARGET_TZ
    );

    if (!combinedDateTime.isValid()) return "";

    return `${combinedDateTime.format("DD-MM-YYYY")}, ${combinedDateTime.format("h:mm a")}`;
  } catch {
    return "";
  }
};